import React, { useState } from 'react';
import { LOCLogo } from '../../assets/images';
import { CustomInput, Modal, Spinner } from '../../components/common';
import { toast } from 'react-toastify';
import useAuth from '../../hooks/useAuth';
import { useCompleteOnboarding, useEmailAddressAvailable } from '../../hooks/useUsers';
import { useVersionedRecord, VersionedRecordKey, useAcceptVersionedRecord } from '../../hooks/useVersionedRecords';

const Onboarding: React.FunctionComponent = (): JSX.Element => {
  const { user } = useAuth();
  const { data: termsOfService } = useVersionedRecord(VersionedRecordKey.CLIENT_ADMIN_TERMS_OF_SERVICE);
  const { data: privacyPolicy } = useVersionedRecord(VersionedRecordKey.CLIENT_ADMIN_PRIVACY_POLICY);
  const { acceptVersionedRecord } = useAcceptVersionedRecord();
  const completeOnboarding = useCompleteOnboarding();

  const [email, setEmail] = useState<string>(user?.email_address ?? '');
  const [firstName, setFirstName] = useState<string>(user?.first_name ?? '');
  const [lastName, setLastName] = useState<string>(user?.last_name ?? '');
  const [password, setPassword] = useState<string>('');
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [error, setError] = useState<any>();
  const [isTerms, setIsTerms] = useState<boolean>(false);
  const [modal, setModal] = useState<'terms' | 'privacy' | undefined>();
  const { data: isAvailableEmail } = useEmailAddressAvailable(email);

  /* TODO: We'll implement password skipping for onboarding with an existing user later, but right now Client Admin
   * Onboarding requires 15 character passwords in the backend, causing problems.
   */
  // const skipPassword = !user?.needs_password;
  const disabled = !isTerms || !firstName || !lastName || !email || !password || !passwordConfirmation;

  const handleSubmit = () => {
    const trimEmail = email?.trim();
    const trimFirstName = firstName?.trim();
    const trimLastName = lastName?.trim();
    const trimPassword = password?.trim();
    const trimPasswordConfirmation = passwordConfirmation?.trim();

    setEmail(trimEmail);
    setFirstName(trimFirstName);
    setLastName(trimLastName);
    setPassword(trimPassword);
    setPasswordConfirmation(trimPasswordConfirmation);

    const emailAddress = user?.email_address;
    const emailRegex = /\S+@\S+\.\S+/;

    const newErrors: Record<string, string> = {};
    if (!emailRegex.test(trimEmail) || trimEmail === '') {
      newErrors.email = 'Enter valid email address';
    } else if (isAvailableEmail === false && emailAddress !== trimEmail) {
      newErrors.email = 'Email already in use';
    }
    if (trimFirstName === undefined || trimFirstName === '') {
      newErrors.firstName = 'Enter first name';
    } else if (trimFirstName.length < 2) {
      newErrors.firstName = 'Enter min. 2 characters';
    }
    if (trimLastName === undefined || trimLastName === '') {
      newErrors.lastName = 'Enter last name';
    } else if (trimLastName.length < 2) {
      newErrors.lastName = 'Enter min. 2 characters';
    }
    if (trimPassword === undefined || trimPassword === '') {
      newErrors.password = 'Enter password';
    }
    if (trimPasswordConfirmation !== trimPassword) {
      newErrors.passwordConfirmation = 'Password does not match';
    }
    if (trimPassword.length < 15) {
      newErrors.password = 'Password must be at least 15 characters';
    }
    if (!isTerms) {
      newErrors.terms = 'Please accept the Terms of Service and Privacy Policy';
    }
    setError(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    if (!user || !termsOfService || !privacyPolicy) {
      console.log('we messed something up');
      return;
    }

    acceptVersionedRecord.mutate({
      versionId: termsOfService.id,
      key: VersionedRecordKey.CLIENT_ADMIN_TERMS_OF_SERVICE,
    });
    acceptVersionedRecord.mutate({
      versionId: privacyPolicy.id,
      key: VersionedRecordKey.CLIENT_ADMIN_PRIVACY_POLICY,
    });

    const input = {
      email_address: email,
      first_name: firstName,
      last_name: lastName,
      password: password,
      userId: user?.id,
    };
    completeOnboarding.mutate(input, {
      onError: (e) => {
        toast.error(e.response.data.description);
      },
    });
  };

  return (
    <div className="bg-white">
      {!termsOfService && <Spinner />}
      <div className="h-screen w-full">
        <div className="text-primary w-full">
          <div className="flex justify-center px-8 py-8">
            <img src={LOCLogo} alt="kindly-logo" className="w-56" />
          </div>
        </div>

        <div className="flex h-full flex-col items-center px-8 py-8">
          <div className="flex w-full max-w-2xl flex-col justify-between bg-white">
            <div className="w-full">
              <h3 className="text-bright-blue mb-4 text-xl font-bold">Create Your Account</h3>
              <div className="mt-4">
                <CustomInput
                  inputType={'text'}
                  value={email}
                  setValue={setEmail}
                  Label={'Email'}
                  Error={error?.email}
                  ErrorMessage={error?.email}
                  labelClass="font-normal"
                  dataTestId="email"
                />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4">
                <CustomInput
                  inputType={'text'}
                  value={firstName}
                  setValue={setFirstName}
                  Label={'First Name'}
                  Error={error?.firstName}
                  ErrorMessage={error?.firstName}
                  labelClass="font-normal"
                  dataTestId="first-name"
                />
                <CustomInput
                  inputType={'text'}
                  value={lastName}
                  setValue={setLastName}
                  Label={'Last Name'}
                  Error={error?.lastName}
                  ErrorMessage={error?.lastName}
                  labelClass="font-normal"
                  dataTestId="last-name"
                />
              </div>
              <div className="mt-4">
                <CustomInput
                  inputType={'password'}
                  value={password}
                  setValue={setPassword}
                  Label={'Password'}
                  Error={error?.password}
                  ErrorMessage={error?.password}
                  labelClass="font-normal"
                  secondLabel="Minimum 15 characters"
                  dataTestId="password"
                />
              </div>
              <div className="mt-4">
                <CustomInput
                  inputType={'password'}
                  value={passwordConfirmation}
                  setValue={setPasswordConfirmation}
                  Label={'Confirm Password'}
                  Error={error?.passwordConfirmation}
                  ErrorMessage={error?.passwordConfirmation}
                  labelClass="font-normal"
                  dataTestId="password-confirmation"
                />
              </div>
              <div className="mt-6">
                <h3 className="text-bright-blue mb-4 text-xl font-bold">Terms and Conditions</h3>
                <div className="mb-6 rounded p-4">
                  <label className="items-top inline-flex">
                    <input
                      type="radio"
                      className="form-radio mt-1 h-5 w-5 text-gray-600"
                      checked={isTerms}
                      onClick={() => setIsTerms(!isTerms)}
                      data-testid="terms-of-service-checkbox"
                    />
                    <span className="ml-2 text-sm leading-5 text-gray-700">
                      I accept the{' '}
                      <button className="text-bright-blue font-bold" onClick={() => setModal('terms')}>
                        Terms of Service
                      </button>{' '}
                      and{' '}
                      <button className="text-bright-blue font-bold" onClick={() => setModal('privacy')}>
                        Privacy Policy
                      </button>
                      . I have read and understood how my data will be processed.
                    </span>
                  </label>
                  {error?.terms && <p className="mt-1 text-sm text-red-500">{error.terms}</p>}
                </div>
              </div>
            </div>
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                className={`rounded-full px-12 py-1.5 text-white ${
                  disabled ? 'bg-gray-background-dark cursor-not-allowed' : 'bg-bright-blue'
                }`}
                onClick={handleSubmit}
                disabled={disabled}
                data-testid="get-started-button"
              >
                GET STARTED
              </button>
            </div>
          </div>
        </div>
      </div>

      <Modal
        isModel={modal === 'terms'}
        InnerComponent={
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white">
            <div className="flex items-center justify-between border-b p-4">
              <h3 className="text-bright-blue text-xl font-bold">Terms of Service</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setModal(undefined)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-4">
              <div
                className="prose prose-black prose-strong:font-bold max-w-none"
                dangerouslySetInnerHTML={{
                  __html: termsOfService?.text || '',
                }}
              ></div>
            </div>
            <div className="flex justify-end border-t p-4">
              <button
                className="bg-bright-blue rounded-full px-6 py-1.5 text-white"
                onClick={() => setModal(undefined)}
              >
                Close
              </button>
            </div>
          </div>
        }
      />
      <Modal
        isModel={modal === 'privacy'}
        InnerComponent={
          <div className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white">
            <div className="flex items-center justify-between border-b p-4">
              <h3 className="text-bright-blue text-xl font-bold">Privacy Policy</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setModal(undefined)}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto p-4">
              <div
                className="prose prose-black prose-strong:font-bold max-w-none"
                dangerouslySetInnerHTML={{
                  __html: privacyPolicy?.text || '',
                }}
              ></div>
            </div>
            <div className="flex justify-end border-t p-4">
              <button
                className="bg-bright-blue rounded-full px-6 py-1.5 text-white"
                onClick={() => setModal(undefined)}
              >
                Close
              </button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Onboarding;
